// ***************************************************************************************
// Tile faces, drawn once into canvases and reused.
//
// No external font service and no image requests during play: the glyphs are drawn with
// the platform's own font stack into an offscreen canvas at load, uploaded as textures,
// and shared by every tile showing that letter. A 5x5 board has 25 tiles and at most 26
// distinct faces, so this is a fixed, small cost paid once.
//
// The tile is drawn INTO the texture rather than assembled from meshes, which is what
// makes the rounded corner and the bottom edge crisp at any zoom without a shader.
//
// MATERIALS ARE SHARED BY EVERY TILE OF THAT LETTER, which is the point - 25 tiles, at
// most 26 faces - and also a trap. Anything that changes a material changes it for every
// tile showing that letter, so an animation that fades ONE tile out must be given a
// material of its own (see `fadeCopy`). This is not hypothetical: the removal animation
// set `opacity` on the shared material, and playing a word containing a P faded every
// other P on the board along with it.
// ***************************************************************************************

import * as THREE from 'three';

import { tileColour } from '../engine/tileColours.ts';

/** Drawn at this size and scaled down by the renderer, so it stays sharp on dense screens. */
const FACE_PIXELS = 256;

const FONT_STACK =
  '800 150px "Inter var", Inter, "SF Pro Display", -apple-system, BlinkMacSystemFont, ' +
  '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export type TileFaceState = 'idle' | 'selected';

/** The app's ink, so a selected letter matches the type around the board. */
const INK = '#14233d';

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
): void {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + w - radius, y);
  context.quadraticCurveTo(x + w, y, x + w, y + radius);
  context.lineTo(x + w, y + h - radius);
  context.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  context.lineTo(x + radius, y + h);
  context.quadraticCurveTo(x, y + h, x, y + h - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
}

function shade(hex: number, amount: number): string {
  const r = (hex >> 16) & 0xff;
  const g = (hex >> 8) & 0xff;
  const b = hex & 0xff;

  const mix = (channel: number): number => {
    const target = amount < 0 ? 0 : 255;
    const t = Math.abs(amount);

    return Math.round(channel + (target - channel) * t);
  };

  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

function drawFace(letter: string, state: TileFaceState): HTMLCanvasElement {
  const canvas = document.createElement('canvas');

  canvas.width = FACE_PIXELS;
  canvas.height = FACE_PIXELS;

  const context = canvas.getContext('2d');

  if (!context) return canvas;

  const colour = tileColour(letter);
  const hex = `#${colour.toString(16).padStart(6, '0')}`;
  const selected = state === 'selected';

  // A small inset so neighbouring tiles read as separate objects without a gap so wide
  // the board looks sparse.
  //
  // THE SAME FOR BOTH STATES, deliberately. A selected tile must occupy exactly the
  // footprint of an unselected one: the tiles are one board unit apart, so anything that
  // grows a tile - a bigger face, a border drawn outside it, a scale-up - puts it on top
  // of its neighbours. Everything that marks a selection below is drawn INSIDE this.
  const inset = FACE_PIXELS * 0.035;
  const size = FACE_PIXELS - inset * 2;
  const radius = FACE_PIXELS * 0.14;

  context.clearRect(0, 0, FACE_PIXELS, FACE_PIXELS);

  // The tactile edge: a darker plate offset downwards, with the face sitting on top. Two
  // or three pixels of it, not a drop shadow and not a bevel - the reference has tiles
  // that look pressed rather than extruded.
  const lip = FACE_PIXELS * 0.028;

  roundedRect(context, inset, inset + lip, size, size, radius);
  context.fillStyle = shade(colour, -0.32);
  context.fill();

  // ***************************************************************************************
  // THE SELECTED TILE.
  //
  // Four goes at this. What it may NOT do is take up more room than an unselected tile,
  // which rules out the obvious answers - growing it, or ringing it in white - because the
  // board is tight and a tile that grows overlaps the ones beside it.
  //
  // So the whole difference is drawn within the same square: the face lifts a little, a
  // white line is inset just inside its edge, and - the part that does most of the work -
  // THE LETTER CHANGES COLOUR, from white to dark ink with a white outline. The letters
  // are the thing the eye is already reading, so changing them is noticed immediately,
  // and an outlined dark glyph holds up on every tile colour in the set, which a plain
  // dark one would not on the purples and blues.
  // ***************************************************************************************
  roundedRect(context, inset, inset, size, size - lip, radius);
  context.fillStyle = selected ? shade(colour, 0.16) : hex;
  context.fill();

  // A whisper of light along the top edge. Without it the face reads flat against the
  // lip; with much more it reads glossy, which the reference is not.
  const sheen = context.createLinearGradient(0, inset, 0, inset + size * 0.45);

  sheen.addColorStop(0, 'rgba(255, 255, 255, 0.16)');
  sheen.addColorStop(1, 'rgba(255, 255, 255, 0)');

  roundedRect(context, inset, inset, size, size - lip, radius);
  context.fillStyle = sheen;
  context.fill();

  if (selected) {
    // Inside the face, not around it. A one-line border that costs no footprint.
    const edge = FACE_PIXELS * 0.03;

    roundedRect(
      context,
      inset + edge,
      inset + edge,
      size - edge * 2,
      size - lip - edge * 2,
      radius - edge / 2,
    );
    context.strokeStyle = 'rgba(255, 255, 255, 0.92)';
    context.lineWidth = edge;
    context.stroke();
  }

  context.font = FONT_STACK;
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  const centreY = inset + (size - lip) / 2 + FACE_PIXELS * 0.008;

  if (selected) {
    // Dark ink, outlined in white so it stays crisp on the dark purples as well as the
    // pale yellows. The outline is drawn first and the fill over it, so the glyph keeps
    // its shape rather than being fattened by its own stroke.
    context.lineWidth = FACE_PIXELS * 0.055;
    context.lineJoin = 'round';
    context.strokeStyle = '#ffffff';
    context.strokeText(letter.toUpperCase(), FACE_PIXELS / 2, centreY);

    context.fillStyle = INK;
    context.fillText(letter.toUpperCase(), FACE_PIXELS / 2, centreY);

    return canvas;
  }

  // A soft dark pad under the glyph keeps white legible on the yellows without an
  // outline, which at this size would look furry.
  context.fillStyle = 'rgba(0, 0, 0, 0.16)';
  context.fillText(letter.toUpperCase(), FACE_PIXELS / 2, centreY + FACE_PIXELS * 0.012);

  context.fillStyle = '#ffffff';
  context.fillText(letter.toUpperCase(), FACE_PIXELS / 2, centreY);

  return canvas;
}

export class TileFaceLibrary {
  private readonly textures = new Map<string, THREE.CanvasTexture>();
  private readonly materials = new Map<string, THREE.MeshBasicMaterial>();

  constructor(private readonly anisotropy: number) {}

  material(letter: string, state: TileFaceState): THREE.MeshBasicMaterial {
    const key = `${letter}:${state}`;
    const existing = this.materials.get(key);

    if (existing) return existing;

    const texture = new THREE.CanvasTexture(drawFace(letter, state));

    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = this.anisotropy;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
    });

    this.textures.set(key, texture);
    this.materials.set(key, material);

    return material;
  }

  /**
   * A private copy of a face, for a tile that is about to be animated on its own.
   *
   * Shares the TEXTURE - the expensive part - and owns only its material state, so
   * fading it affects one tile. The caller disposes it when the animation is over;
   * disposing a material does not touch the map it points at, which is what makes this
   * safe.
   */
  fadeCopy(letter: string, state: TileFaceState): THREE.MeshBasicMaterial {
    const copy = this.material(letter, state).clone();

    copy.transparent = true;

    return copy;
  }

  dispose(): void {
    for (const material of this.materials.values()) material.dispose();
    for (const texture of this.textures.values()) texture.dispose();

    this.materials.clear();
    this.textures.clear();
  }
}
